import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Trophy,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  Search,
  Download,
  Printer,
  RefreshCw,
  AlertCircle,
  Medal,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  Flag,
  Users,
  Settings,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input, Select } from '../../components/ui/Input';
import { useSeries } from '../../App';

// Mock standings data - would come from API
const mockStandings = [
  { rank: 1, driver_id: 1, name: 'Brad Sweet', car_number: '49', team: 'Kasey Kahne Racing', points: 3456, wins: 8, top5: 22, top10: 32, starts: 42, behind: 0, change: 0 },
  { rank: 2, driver_id: 2, name: 'David Gravel', car_number: '2', team: 'Big Game Motorsports', points: 3398, wins: 6, top5: 20, top10: 30, starts: 42, behind: -58, change: 1 },
  { rank: 3, driver_id: 3, name: 'Carson Macedo', car_number: '41', team: 'Jason Johnson Racing', points: 3352, wins: 5, top5: 19, top10: 28, starts: 42, behind: -104, change: -1 },
  { rank: 4, driver_id: 4, name: 'Donny Schatz', car_number: '15', team: 'Tony Stewart Racing', points: 3298, wins: 4, top5: 18, top10: 29, starts: 42, behind: -158, change: 0 },
  { rank: 5, driver_id: 5, name: 'Logan Schuchart', car_number: '1S', team: 'Shark Racing', points: 3245, wins: 3, top5: 17, top10: 27, starts: 42, behind: -211, change: 2 },
  { rank: 6, driver_id: 6, name: 'James McFadden', car_number: '9', team: 'Kasey Kahne Racing', points: 3201, wins: 3, top5: 15, top10: 25, starts: 42, behind: -255, change: -1 },
  { rank: 7, driver_id: 7, name: 'Sheldon Haudenschild', car_number: '17', team: 'Stenhouse Jr. / Marshall Racing', points: 3156, wins: 2, top5: 14, top10: 24, starts: 42, behind: -300, change: -1 },
  { rank: 8, driver_id: 8, name: 'Giovanni Scelzi', car_number: '18', team: 'KCP Racing', points: 3102, wins: 2, top5: 12, top10: 22, starts: 42, behind: -354, change: 1 },
  { rank: 9, driver_id: 9, name: 'Spencer Bayston', car_number: '5', team: 'CJB Motorsports', points: 3055, wins: 1, top5: 11, top10: 21, starts: 42, behind: -401, change: -1 },
  { rank: 10, driver_id: 10, name: 'Brent Marks', car_number: '19', team: 'Brent Marks Racing', points: 2998, wins: 1, top5: 10, top10: 20, starts: 42, behind: -458, change: 0 },
  { rank: 11, driver_id: 11, name: 'Jacob Allen', car_number: '1A', team: 'Shark Racing', points: 2945, wins: 0, top5: 8, top10: 18, starts: 42, behind: -511, change: 1 },
  { rank: 12, driver_id: 12, name: 'Bill Balog', car_number: '17B', team: 'B Squared Motorsports', points: 2890, wins: 0, top5: 7, top10: 16, starts: 42, behind: -566, change: -1 },
  { rank: 13, driver_id: 13, name: 'Kraig Kinser', car_number: '11K', team: 'Kinser Racing', points: 2834, wins: 0, top5: 6, top10: 15, starts: 42, behind: -622, change: 0 },
  { rank: 14, driver_id: 14, name: 'Justin Peck', car_number: '13', team: 'Buch Motorsports', points: 2778, wins: 0, top5: 5, top10: 14, starts: 42, behind: -678, change: 2 },
  { rank: 15, driver_id: 15, name: 'Parker Price-Miller', car_number: '7BC', team: 'Jared Zimbardi Racing', points: 2720, wins: 0, top5: 4, top10: 12, starts: 42, behind: -736, change: -1 },
];

// Mock recent race results for the leader
const mockRecentResults = [
  { event: 'Eldora Speedway', date: '2026-07-15', position: 1, points_earned: 150 },
  { event: 'Knoxville Raceway', date: '2026-07-12', position: 3, points_earned: 144 },
  { event: 'Williams Grove', date: '2026-07-08', position: 2, points_earned: 146 },
  { event: 'Port Royal Speedway', date: '2026-07-05', position: 5, points_earned: 140 },
  { event: 'Lincoln Speedway', date: '2026-07-01', position: 1, points_earned: 150 },
];

// Position indicator component
function PositionChange({ change }) {
  if (change > 0) {
    return (
      <div className="flex items-center gap-1 text-emerald-600">
        <TrendingUp className="w-4 h-4" />
        <span className="text-xs font-medium">+{change}</span>
      </div>
    );
  } else if (change < 0) {
    return (
      <div className="flex items-center gap-1 text-red-500">
        <TrendingDown className="w-4 h-4" />
        <span className="text-xs font-medium">{change}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 text-slate-400">
      <Minus className="w-4 h-4" />
    </div>
  );
}

// Rank badge component
function RankBadge({ rank }) {
  if (rank === 1) {
    return (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg">
        <Trophy className="w-5 h-5 text-white" />
      </div>
    );
  } else if (rank === 2) {
    return (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-300 to-slate-500 flex items-center justify-center shadow-md">
        <Medal className="w-5 h-5 text-white" />
      </div>
    );
  } else if (rank === 3) {
    return (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center shadow-md">
        <Medal className="w-5 h-5 text-white" />
      </div>
    );
  }
  return (
    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center border border-slate-200">
      <span className="font-bold text-slate-600">{rank}</span>
    </div>
  );
}

// Standings row component
function StandingsRow({ standing, isExpanded, onToggle, seriesSlug }) {
  const isTopThree = standing.rank <= 3;

  return (
    <>
      <tr
        className={`border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer ${
          isTopThree ? 'bg-gradient-to-r from-amber-50/50 to-transparent' : ''
        }`}
        onClick={onToggle}
      >
        {/* Rank */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-3">
            <RankBadge rank={standing.rank} />
            <PositionChange change={standing.change} />
          </div>
        </td>

        {/* Car Number */}
        <td className="px-4 py-3">
          <span className="font-mono font-bold text-lg text-slate-800">
            #{standing.car_number}
          </span>
        </td>

        {/* Driver */}
        <td className="px-4 py-3">
          <div>
            <Link
              to={`/${seriesSlug}/competitors/${standing.driver_id}`}
              className="font-semibold text-slate-800 hover:text-rms-accent transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {standing.name}
            </Link>
            <div className="text-xs text-slate-500">{standing.team}</div>
          </div>
        </td>

        {/* Points */}
        <td className="px-4 py-3 text-center">
          <span className={`text-xl font-bold ${isTopThree ? 'text-amber-600' : 'text-slate-800'}`}>
            {standing.points.toLocaleString()}
          </span>
        </td>

        {/* Behind */}
        <td className="px-4 py-3 text-center">
          {standing.behind === 0 ? (
            <Badge variant="success" size="sm">Leader</Badge>
          ) : (
            <span className="text-slate-500">{standing.behind}</span>
          )}
        </td>

        {/* Wins */}
        <td className="px-4 py-3 text-center">
          <span className={`font-semibold ${standing.wins > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
            {standing.wins}
          </span>
        </td>

        {/* Top 5 */}
        <td className="px-4 py-3 text-center">
          <span className="text-slate-600">{standing.top5}</span>
        </td>

        {/* Top 10 */}
        <td className="px-4 py-3 text-center">
          <span className="text-slate-600">{standing.top10}</span>
        </td>

        {/* Starts */}
        <td className="px-4 py-3 text-center">
          <span className="text-slate-600">{standing.starts}</span>
        </td>

        {/* Expand */}
        <td className="px-4 py-3">
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          )}
        </td>
      </tr>

      {/* Expanded Details */}
      {isExpanded && (
        <tr className="bg-slate-50">
          <td colSpan={10} className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Stats Summary */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-700">Season Statistics</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-white rounded-lg border border-slate-200">
                    <div className="text-xs text-slate-500">Win %</div>
                    <div className="text-lg font-bold text-slate-800">
                      {standing.starts > 0 ? ((standing.wins / standing.starts) * 100).toFixed(1) : 0}%
                    </div>
                  </div>
                  <div className="p-3 bg-white rounded-lg border border-slate-200">
                    <div className="text-xs text-slate-500">Avg. Finish</div>
                    <div className="text-lg font-bold text-slate-800">
                      {(standing.starts > 0 ? (standing.top5 * 3 + standing.top10 * 7) / standing.starts : 0).toFixed(1)}
                    </div>
                  </div>
                  <div className="p-3 bg-white rounded-lg border border-slate-200">
                    <div className="text-xs text-slate-500">Top 5 %</div>
                    <div className="text-lg font-bold text-slate-800">
                      {standing.starts > 0 ? ((standing.top5 / standing.starts) * 100).toFixed(1) : 0}%
                    </div>
                  </div>
                  <div className="p-3 bg-white rounded-lg border border-slate-200">
                    <div className="text-xs text-slate-500">Pts/Race</div>
                    <div className="text-lg font-bold text-slate-800">
                      {standing.starts > 0 ? (standing.points / standing.starts).toFixed(1) : 0}
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Results */}
              <div className="space-y-3 md:col-span-2">
                <h4 className="text-sm font-semibold text-slate-700">Recent Results</h4>
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-100 text-xs text-slate-500">
                        <th className="px-3 py-2 text-left">Event</th>
                        <th className="px-3 py-2 text-center">Finish</th>
                        <th className="px-3 py-2 text-right">Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mockRecentResults.slice(0, 5).map((result, idx) => (
                        <tr key={idx} className="border-t border-slate-100 text-sm">
                          <td className="px-3 py-2 text-slate-700">{result.event}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`font-semibold ${
                              result.position === 1 ? 'text-amber-600' :
                              result.position <= 3 ? 'text-emerald-600' :
                              'text-slate-600'
                            }`}>
                              P{result.position}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right font-medium text-slate-600">
                            +{result.points_earned}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function PointsStandings() {
  useParams();
  const currentSeries = useSeries();
  const seriesSlug = currentSeries?.slug;

  // eslint-disable-next-line no-unused-vars
  const [standings, setStandings] = useState(mockStandings);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState('2026');

  const series = currentSeries || { name: 'Unknown Series', color: '#3b5998' };

  // Filter standings by search
  const filteredStandings = standings.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.car_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.team.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleRow = (rank) => {
    setExpandedRow(expandedRow === rank ? null : rank);
  };

  const handleRefresh = () => {
    setLoading(true);
    // Simulate refresh
    setTimeout(() => setLoading(false), 1000);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    alert('Export feature coming soon');
  };

  // Stats for header
  const leader = standings[0];
  const totalRaces = leader?.starts || 0;
  const totalDrivers = standings.length;

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-6">
        <Link
          to={`/${seriesSlug}/season`}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Trophy className="w-6 h-6 text-amber-500" />
              <h1 className="text-2xl font-bold text-slate-800">Points Standings</h1>
            </div>
            <p className="text-slate-500">{series.name} - {selectedSeason} Season Championship</p>
          </div>

          <div className="flex items-center gap-2">
            <Select
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
              className="w-32"
            >
              <option value="2026">2026</option>
              <option value="2025">2025</option>
              <option value="2024">2024</option>
            </Select>
            <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={handleExport}>
              <Download className="w-4 h-4 mr-1" />
              Export
            </Button>
            <Button variant="ghost" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-1" />
              Print
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {/* Points Leader */}
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center shadow-lg">
                <Trophy className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-xs text-amber-700 font-medium">Points Leader</p>
                <p className="text-lg font-bold text-slate-800">{leader?.name}</p>
                <p className="text-sm text-amber-600 font-semibold">{leader?.points.toLocaleString()} pts</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{totalRaces}</p>
                <p className="text-xs text-slate-500">Races Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{totalDrivers}</p>
                <p className="text-xs text-slate-500">Drivers Ranked</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Flag className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">
                  {standings.filter(s => s.wins > 0).length}
                </p>
                <p className="text-xs text-slate-500">Different Winners</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search drivers, car numbers, or teams..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Link to={`/${seriesSlug}/config/points`}>
              <Button variant="ghost" size="sm">
                <Settings className="w-4 h-4 mr-1" />
                Points Config
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Standings Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : filteredStandings.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-100 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    <th className="px-4 py-3 text-left">Rank</th>
                    <th className="px-4 py-3 text-left">Car</th>
                    <th className="px-4 py-3 text-left">Driver</th>
                    <th className="px-4 py-3 text-center">Points</th>
                    <th className="px-4 py-3 text-center">Behind</th>
                    <th className="px-4 py-3 text-center">Wins</th>
                    <th className="px-4 py-3 text-center">Top 5</th>
                    <th className="px-4 py-3 text-center">Top 10</th>
                    <th className="px-4 py-3 text-center">Starts</th>
                    <th className="px-4 py-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStandings.map((standing) => (
                    <StandingsRow
                      key={standing.rank}
                      standing={standing}
                      isExpanded={expandedRow === standing.rank}
                      onToggle={() => handleToggleRow(standing.rank)}
                      seriesSlug={seriesSlug}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">No Results Found</h3>
              <p className="text-slate-400">Try adjusting your search criteria</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="mt-4 flex items-center gap-6 text-sm text-slate-500">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-emerald-600" />
          <span>Moved up</span>
        </div>
        <div className="flex items-center gap-2">
          <TrendingDown className="w-4 h-4 text-red-500" />
          <span>Moved down</span>
        </div>
        <div className="flex items-center gap-2">
          <Minus className="w-4 h-4 text-slate-400" />
          <span>No change</span>
        </div>
        <div className="ml-auto text-xs text-slate-400">
          Click any row to expand for more details
        </div>
      </div>
    </div>
  );
}
